import React, { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextStyle, ViewStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../context/ThemeContext';
import { ctaShadow, ctaSurface } from './CtaGradientFill';
import { fontWeights } from '../../styles/typographyTokens';

import { ON_ACCENT_TEXT } from '../../styles/accentText';
interface GradientCtaButtonProps {
  children: React.ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  style?: ViewStyle | any;
  textStyle?: TextStyle | any;
  testID?: string;
  /** Optional leading icon (e.g. a broadcast glyph). Rendered before the text. */
  leftIcon?: keyof typeof Ionicons.glyphMap;
  /** Trailing forward arrow. Default true keeps existing call sites unchanged. */
  showArrow?: boolean;
}

export const GradientCtaButton: React.FC<GradientCtaButtonProps> = ({
  children,
  onPress,
  disabled = false,
  isLoading = false,
  style,
  textStyle,
  testID,
  leftIcon,
  showArrow = true,
}) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || isLoading;
  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled}
      onHoverIn={Platform.OS === 'web' ? () => setHovered(true) : undefined}
      onHoverOut={Platform.OS === 'web' ? () => setHovered(false) : undefined}
      style={({ pressed }) => [
        styles.touch,
        Platform.OS === 'web' && { boxShadow: colors.ctaShadow },
        hovered && !isDisabled && styles.hovered,
        pressed && !isDisabled && styles.pressed,
        isDisabled && styles.disabled,
        style,
      ]}
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled, busy: isLoading }}
      testID={testID}
    >
      {({ pressed }) => (
        <LinearGradient
          colors={pressed
            ? [colors.ctaPressedStart, colors.ctaPressedEnd]
            : hovered
              ? [colors.ctaHoverStart, colors.ctaHoverEnd]
              : [colors.ctaStart, colors.ctaEnd]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={styles.fill}
        >
          {isLoading ? (
            <ActivityIndicator color={ON_ACCENT_TEXT} />
          ) : (
            <>
              {leftIcon ? <Ionicons name={leftIcon} size={22} color={ON_ACCENT_TEXT} /> : null}
              <Text style={[styles.text, textStyle]} numberOfLines={1}>{children}</Text>
              {showArrow ? <Ionicons name="arrow-forward" size={21} color={ON_ACCENT_TEXT} /> : null}
            </>
          )}
        </LinearGradient>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  touch: {
    minHeight: 56,
    borderRadius: 28,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
      transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease',
    } as any : {
      // Native counterpart of `colors.ctaShadow` (a CSS box-shadow string RN
      // can't parse). Android needs an opaque background for elevation to
      // cast anything — the gradient covers it, so it's never seen.
      ...ctaSurface,
      ...ctaShadow,
      elevation: 10,
    }),
  },
  fill: {
    minHeight: 56,
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 11,
  },
  text: {
    color: ON_ACCENT_TEXT,
    fontSize: 17,
    lineHeight: 22,
    fontWeight: fontWeights.bold,
  } as any,
  disabled: {
    opacity: 0.45,
  },
  hovered: {
    transform: [{ translateY: -2 }],
  },
  pressed: {
    transform: [{ translateY: 0 }, { scale: 0.985 }],
  },
});
